import { CommonForm } from '@/components/CommonForm';
import { CommonInput } from '@/components/CommonForm/CommonInput';
import { CommonPhoneInput } from '@/components/CommonForm/CommonPhoneInput';
import { useEnv } from '@/env/client';
import { useForm } from '@/hooks/useForm';
import { getDynamicsTopicConsents } from '@/tools/forms';
import { withSuspense } from '@/tools/withSuspense';
import { DynamicsLeadSource, DynamicsLeadType, IDynamicsFieldMapping } from '@/types/dynamicsForms';
import { FormTag } from '@/types/forms';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RichText } from '@lamesarv-sdk/components';
import { navigate, parsePhone } from '@lamesarv-sdk/tools';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { BaseOptInCheckbox, BaseSubmit } from '../base';
import { IFormProps, IFormResetterProps } from '../types';
import { IFormSchema, validationSchema } from './schema';

const FormResetter = (props: IFormResetterProps) => {
  const { reset } = useFormContext();

  useEffect(() => {
    if (!props.wasSuccessful) return;

    reset();
    reset({ phone: '' });

    if (props.callback) {
      props.callback();
    }

    if (props.redirect) {
      navigate(props.redirect);
    }
  }, [props.wasSuccessful]);

  return null;
};

export const FormStayUpdated = withSuspense((props: IFormProps) => {
  const { env } = useEnv();
  const { isLoading, hasError, submitted, sendForm } = useForm();

  const onSubmit = async (data: IFormSchema, metadata?: Record<string, unknown>) => {
    const formInput = { ...data, extra_tag_ids: [FormTag.STAY_UPDATED] };

    const mappings: IDynamicsFieldMapping[] = [
      { DataverseFieldName: 'lmrv_leadtype', DataverseFieldValue: String(DynamicsLeadType.marketing) },
      {
        DataverseFieldName: 'leadsourcecode',
        DataverseFieldValue: String(DynamicsLeadSource['website-online-inquiry']),
      },
      { DataverseFieldName: 'firstname', DataverseFieldValue: formInput.firstName },
      { DataverseFieldName: 'lastname', DataverseFieldValue: formInput.lastName },
      { DataverseFieldName: 'mobilephone', DataverseFieldValue: parsePhone(formInput.phone) },
      { DataverseFieldName: 'emailaddress1', DataverseFieldValue: formInput.email },
      { DataverseFieldName: 'crbdb_segment', DataverseFieldValue: props.dynamics_segment || '' },
    ];

    // Special Form: Automatically add Email topic channel consent
    const topicChannels = ['Email', formInput.smsOptIn ? 'Text' : undefined].filter(Boolean).join(',');
    const topicConsents = getDynamicsTopicConsents({
      complianceProfileId: env.DYNAMICS_COMPLIANCE_PROFILE_ID || '',
      topicIDs: env.DYNAMICS_TOPIC_IDS || '',
      channels: topicChannels,
    });

    mappings.push(...topicConsents);

    if (env.ENABLE_FORMS_CAPTCHA && env.ENABLE_FORMS_CAPTURE_CAPTCHA_SCORE) {
      mappings.push(
        {
          DataverseFieldName: 'lmrv_captchascore',
          DataverseFieldValue: metadata?.captchaScore ? String(metadata?.captchaScore) : '',
        },
        {
          DataverseFieldName: 'lmrv_captchathreshold',
          DataverseFieldValue: metadata?.captchaThreshold ? String(metadata?.captchaThreshold) : '',
        },
      );
    }

    await sendForm({
      formKey: 'NEWSLETTER',
      formName: props.custom_name || 'Form Stay Updated',
      mappings,
    });
  };

  const wasSuccessful = !isLoading && !hasError && submitted;

  return (
    <CommonForm
      id={props.custom_id || 'formStayUpdated'}
      schema={validationSchema}
      onSubmit={onSubmit}
      className={twMerge('font-helvetica relative', props.classes, isLoading && 'opacity-50')}
      isDisabled={isLoading || wasSuccessful}
      tagGroup={props.data_tag_group || 'form'}
      tagItem={props.data_tag ? `${props.data_tag}--form-stay-updated` : 'form-stay-updated'}
    >
      <FormResetter wasSuccessful={wasSuccessful} callback={props.successCallback} redirect={props.redirect_url} />
      {props.extract_header && (
        <div
          className={twMerge(
            'w-full md:w-[720px] xl:w-[865px] mx-auto flex flex-col gap-4 p-1 md:p-8 text-center justify-center',
            props.classes_header,
          )}
        >
          <div className={twMerge('heading-h4 leading-none', props.classes_title)}>{props.title || 'Stay Updated'}</div>
          <div className="text-md-medium leading-none">All fields marked with an asterisk (*) are required.</div>
          {props.description && (
            <div className={twMerge('text-md-regular', props.classes_description)}>
              <RichText content={props.description} />
            </div>
          )}
          {!props.description && (
            <div className={twMerge('text-md-regular', props.classes_description)}>
              Stay informed on new arrivals and best deals!
            </div>
          )}
        </div>
      )}
      <div
        className={twMerge(
          'w-full flex flex-col gap-5 p-1 md:p-8 bg-white md:border border-black-300 rounded-lg',
          hasError && 'p-8 border border-red-500',
          props.classes_container,
        )}
      >
        {!props.extract_header && (
          <div className={twMerge('flex flex-col justify-center items-center gap-4', props.classes_header)}>
            <div className={twMerge('heading-h4 leading-none', props.classes_title)}>
              {props.title || 'Stay Updated'}
            </div>
            {props.description && (
              <div className={twMerge('text-md-regular', props.classes_description)}>
                <RichText content={props.description} />
              </div>
            )}
            {!props.description && (
              <div className={twMerge('text-md-regular', props.classes_description)}>
                Stay informed on new arrivals and best deals!
              </div>
            )}
          </div>
        )}
        <div className={twMerge('w-full flex flex-col gap-5 mx-auto', props.classes_content)}>
          <div className="flex flex-col gap-5 md:flex-row md:items-start md:gap-6">
            <div className="flex flex-col gap-2 md:flex-1">
              <CommonInput
                type="text"
                label="First Name"
                placeholder="First Name *"
                name="firstName"
                classInput="rounded-none border-0 border-b border-black-200 focus:outline-none focus:ring-0 py-2 px-0 text-sm md:text-base md:py-3 placeholder-black-300"
                hideLabel
                isRequired
              />
            </div>
            <div className="flex flex-col gap-2 md:flex-1">
              <CommonInput
                type="text"
                label="Last Name"
                placeholder="Last Name *"
                name="lastName"
                classInput="rounded-none border-0 border-b border-black-200 focus:outline-none focus:ring-0 py-2 px-0 text-sm md:text-base md:py-3 placeholder-black-300"
                hideLabel
                isRequired
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <CommonPhoneInput
              label="Phone"
              placeholder="Phone"
              name="phone"
              classInput="rounded-none border-0 border-b border-black-200 focus:outline-none focus:ring-0 py-2 px-0 text-sm md:text-base md:py-3 placeholder-black-300"
              hideLabel
            />
          </div>
          <div className="flex flex-col gap-2">
            <div className="w-full relative flex flex-row items-center">
              <FontAwesomeIcon
                icon={faEnvelope}
                className="absolute left-0 top-2.5 md:top-4 size-4 shrink-0 md:size-5"
              />

              <CommonInput
                type="email"
                label="Email"
                placeholder="Email *"
                name="email"
                classContainer="w-full"
                classInput="rounded-none border-0 border-b border-black-200 focus:outline-none focus:ring-0 py-2 pl-8 pr-16 text-sm md:text-base md:py-3 placeholder-black-300"
                hideLabel
                isRequired
              />
              <BaseSubmit
                {...props}
                formNameSlug="form-stay-updated"
                styleClasses="absolute right-0 top-2.5 md:top-4 text-sm md:text-base font-bold disabled:opacity-50"
              />
            </div>
            {props.display_sms_checkbox && (
              <div className="flex flex-row py-2">
                <BaseOptInCheckbox name="smsOptIn" nameDependency="phone" htmlContent={props.custom_sms_text} />
              </div>
            )}
          </div>
          {wasSuccessful && (
            <span
              className="text-xs md:text-sm mt-10 md:mt-5 text-center"
              data-tag-group="form-success-label"
              data-tag={
                props.data_tag
                  ? `${props.data_tag}--form-stay-updated-success-label`
                  : 'form-stay-updated-success-label'
              }
            >
              Thank you for Signing Up!
            </span>
          )}
        </div>
      </div>
    </CommonForm>
  );
});
